import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Heart } from "lucide-react";
import { settingService } from "../../services/settingService";

const WhatsApp = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.512 5.26l-.999 3.648 3.736-.979zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
  </svg>
);

const Facebook = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.927-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
  </svg>
);

const toWhatsAppUrl = (val) => {
  if (!val) return null;
  if (val.startsWith('http')) return val;
  const digits = val.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : null;
};

const FALLBACK_SOCIAL = {
  social_instagram: "https://www.instagram.com/buranshikes?igsh=MWpiZW9kcmxuc2Zqdw==",
  social_whatsapp:  "919068676184",
  social_facebook:  "",
};

const Footer = () => {
  const [social, setSocial] = useState(FALLBACK_SOCIAL);

  useEffect(() => {
    settingService.getPublicSettings()
      .then(res => {
        const map = {};
        (res.data.settings || []).forEach(s => { map[s.key] = s.value; });
        setSocial(prev => ({ ...prev, ...map }));
      })
      .catch(() => {});
  }, []);

  const socialLinks = [
    { key: "social_facebook",  Icon: Facebook,  label: "Facebook",  href: social.social_facebook || null },
    { key: "social_instagram", Icon: Instagram, label: "Instagram", href: social.social_instagram || null },
    { key: "social_whatsapp",  Icon: WhatsApp,  label: "WhatsApp",  href: toWhatsAppUrl(social.social_whatsapp) },
  ].filter(l => l.href);

  const footerStyle = { backgroundColor: "#1f2937", color: "#d1d5db" };
  const containerStyle = { maxWidth: 1280, margin: "0 auto", padding: "64px 24px" };
  const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 40 };
  const headingStyle = { fontSize: 15, fontWeight: 600, color: "#ffffff", marginBottom: 20 };
  const linkStyle = { display: "block", fontSize: 13, color: "#9ca3af", textDecoration: "none", marginBottom: 10, transition: "color 0.2s" };
  const iconBtnStyle = { width: 34, height: 34, borderRadius: 8, border: "1px solid #374151", backgroundColor: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#9ca3af" };

  return (
    <footer style={footerStyle}>
      <div style={containerStyle}>
        <div style={gridStyle}>
          {/* Brand */}
          <div>
            <p style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.7, marginBottom: 20 }}>
              Your perfect mountain retreat nestled in the pristine hills of Dhanaulti at 2,280m altitude.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              {socialLinks.map(({ key, Icon, label, href }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  style={iconBtnStyle}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={headingStyle}>Quick Links</h4>
            {[
              { to: "/", label: "Home" },
              { to: "/about", label: "About Us" },
              { to: "/rooms", label: "Rooms & Cottage" },
              { to: "/gallery", label: "Gallery" },
              { to: "/contact", label: "Contact" },
            ].map(({ to, label }) => (
              <Link key={to} to={to} style={linkStyle} onClick={() => window.scrollTo(0, 0)}>
                → {label}
              </Link>
            ))}
            <a href="/#how-to-reach" style={linkStyle}>→ How to Reach</a>
          </div>

          {/* Services */}
          <div>
            <h4 style={headingStyle}>Our Services</h4>
            {["Room Booking", "Mountain Trekking", "Bonfire Evenings", "Nature Walks", "Photography Tours", "DJ Night"].map(s => (
              <p key={s} style={{ fontSize: 13, color: "#9ca3af", marginBottom: 10 }}>→ {s}</p>
            ))}
          </div>

          {/* Contact */}
          <div>
            <h4 style={headingStyle}>Contact Us</h4>
            <div style={{ marginBottom: 14, display: "flex", gap: 10, alignItems: "flex-start" }}>
              <MapPin size={15} color="#16a34a" style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.6 }}>
                Buranskhanda, Near Dhanaulti, Uttarakhand 248001
              </span>
            </div>
            <div style={{ marginBottom: 14, display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Phone size={15} color="#16a34a" style={{ flexShrink: 0, marginTop: 3 }} />
              <div>
                <span style={{ fontSize: 13, color: "#9ca3af", display: "block" }}>+91 8218579930</span>
                <span style={{ fontSize: 13, color: "#9ca3af", display: "block" }}>+91 9068676184</span>
              </div>
            </div>
            <div style={{ marginBottom: 24, display: "flex", gap: 10, alignItems: "center" }}>
              <Mail size={15} color="#16a34a" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "#9ca3af" }}>suryaprataps375@gmail.com</span>
            </div>
            <Link
              to="/register"
              style={{ display: "block", padding: "9px 0", textAlign: "center", backgroundColor: "#16a34a", color: "#ffffff", borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: "none", boxShadow: "0 2px 8px rgba(22,163,74,0.35)" }}
            >
              Book Your Stay
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div style={{ borderTop: "1px solid #374151" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <p style={{ fontSize: 12, color: "#6b7280" }}>© Burans Hikes Home Stay (Resort). All rights reserved.</p>
          <p style={{ fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
            Crafted with <Heart size={12} color="#fff" />
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
